import { alert } from '@repo/ui/Alert'
import { debounce } from '@repo/utils/debounce'
import { formatThousands } from '@repo/utils/numbers'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeWrapper } from '../storeWrapper'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'

const initialState = {
  mattersTableData: []
}

export const useMatterTableStore = storeWrapper('useMatterTableStore', (set) => ({
  ...initialState,
  fetchMattersTableData: async () => {
    return new Promise((resolve) => {
      const { mattersTableFilters } = useDataGridFilterStore.getState()

      debounce({
        id: `fetchMatterTableData_${mattersTableFilters.clientMatterNo}`,
        delay: 300,
        callback: async () => {
          let data

          data = {
            orderByColumns: mattersTableFilters.orderByColumns ?? null,
            pageSize: mattersTableFilters.pageSize ?? 25,
            startAtIndex: mattersTableFilters.startAtIndex ?? 0,

            archiveLocation: mattersTableFilters.filters.archiveLocation ?? null,
            archiveReference: mattersTableFilters.filters.archiveReference ?? null,
            branchId: mattersTableFilters.filters.branchId ?? null,
            clientBalance: mattersTableFilters.filters.clientBalance ?? null,
            clientName: mattersTableFilters.filters.clientName ?? null,
            clientNo: mattersTableFilters.filters.clientNo ?? null,
            clientType: mattersTableFilters.filters.clientType ?? null,
            dateArchived: mattersTableFilters.filters.dateArchived ?? null,
            dateCompleted: mattersTableFilters.filters.dateCompleted ?? null,
            dateOpened: mattersTableFilters.filters.dateOpened ?? null,
            departmentId: mattersTableFilters.filters.departmentId ?? null,
            disbursementsBalance: mattersTableFilters.filters.disbursementsBalance ?? null,
            feeEarnerIds: mattersTableFilters.filters.feeEarnerIds ?? null,
            highRisk: mattersTableFilters.filters.highRisk ?? null,
            isArchived: mattersTableFilters.filters.isArchived ?? null,
            isClientPortalEnabled: mattersTableFilters.filters.isClientPortalEnabled ?? null,
            isCompleted: mattersTableFilters.filters.isCompleted ?? null,
            isProspect: mattersTableFilters.filters.isProspect ?? null,
            isUserRestricted: mattersTableFilters.filters.isUserRestricted ?? null,
            lastMatterDate: mattersTableFilters.filters.lastMatterDate ?? null,
            matterDescription: mattersTableFilters.filters.matterDescription ?? null,
            matterNo: mattersTableFilters.filters.matterNo ?? null,
            matterWipBalance: mattersTableFilters.filters.matterWipBalance ?? null,
            nextKeyDate: mattersTableFilters.filters.nextKeyDate ?? null,
            officeBalance: mattersTableFilters.filters.officeBalance ?? null,
            privateOrLegalAid: mattersTableFilters.filters.privateOrLegalAid ?? null,
            prospectLiveDate: mattersTableFilters.filters.prospectLiveDate ?? null,
            reviewDate: mattersTableFilters.filters.reviewDate ?? null,
            supervisingFeeEarnerIds: mattersTableFilters.filters.supervisingFeeEarnerIds ?? null,
            workflowStatus: mattersTableFilters.filters.workflowStatus ?? null,
            workTypeIds: mattersTableFilters.filters.workTypeIds ?? null
          }

          let fetchedMatterTableData

          try {
            fetchedMatterTableData =
              (await ospreyAPI({
                method: 'POST',
                service: 'matters',
                endpoint: 'Matter/GetAllCompleteBySearchCriteria',
                data: data
              })) || {}
          } catch (err) {
            console.log('Error:', err)
            return
          }

          set({ mattersTableData: fetchedMatterTableData })
          resolve(fetchedMatterTableData)
        }
      })
    })
  },
  fetchMattersTableAsCSV: async () => {
    const { mattersTableFilters } = useDataGridFilterStore.getState()

    return new Promise((resolve) => {
      debounce({
        id: `fetchMatterTableData_CSV`,
        delay: 300,
        callback: async () => {
          let data

          data = {
            orderByColumns: mattersTableFilters.orderByColumns ?? null,
            pageSize: mattersTableFilters.pageSize ?? 25,
            startAtIndex: mattersTableFilters.startAtIndex ?? 0,

            archiveLocation: mattersTableFilters.filters.archiveLocation ?? null,
            archiveReference: mattersTableFilters.filters.archiveReference ?? null,
            branchId: mattersTableFilters.filters.branchId ?? null,
            clientBalance: mattersTableFilters.filters.clientBalance ?? null,
            clientName: mattersTableFilters.filters.clientName ?? null,
            clientNo: mattersTableFilters.filters.clientNo ?? null,
            clientType: mattersTableFilters.filters.clientType ?? null,
            dateArchived: mattersTableFilters.filters.dateArchived ?? null,
            dateCompleted: mattersTableFilters.filters.dateCompleted ?? null,
            dateOpened: mattersTableFilters.filters.dateOpened ?? null,
            departmentId: mattersTableFilters.filters.departmentId ?? null,
            disbursementsBalance: mattersTableFilters.filters.disbursementsBalance ?? null,
            feeEarnerIds: mattersTableFilters.filters.feeEarnerIds ?? null,
            highRisk: mattersTableFilters.filters.highRisk ?? null,
            isArchived: mattersTableFilters.filters.isArchived ?? null,
            isClientPortalEnabled: mattersTableFilters.filters.isClientPortalEnabled ?? null,
            isCompleted: mattersTableFilters.filters.isCompleted ?? null,
            isProspect: mattersTableFilters.filters.isProspect ?? null,
            isUserRestricted: mattersTableFilters.filters.isUserRestricted ?? null,
            lastMatterDate: mattersTableFilters.filters.lastMatterDate ?? null,
            matterDescription: mattersTableFilters.filters.matterDescription ?? null,
            matterNo: mattersTableFilters.filters.matterNo ?? null,
            matterWipBalance: mattersTableFilters.filters.matterWipBalance ?? null,
            nextKeyDate: mattersTableFilters.filters.nextKeyDate ?? null,
            officeBalance: mattersTableFilters.filters.officeBalance ?? null,
            privateOrLegalAid: mattersTableFilters.filters.privateOrLegalAid ?? null,
            prospectLiveDate: mattersTableFilters.filters.prospectLiveDate ?? null,
            reviewDate: mattersTableFilters.filters.reviewDate ?? null,
            supervisingFeeEarnerIds: mattersTableFilters.filters.supervisingFeeEarnerIds ?? null,
            workflowStatus: mattersTableFilters.filters.workflowStatus ?? null,
            workTypeIds: mattersTableFilters.filters.workTypeIds ?? null
          }

          let fetchedMattersTableDataCSV

          try {
            fetchedMattersTableDataCSV =
              (await ospreyAPI({
                method: 'POST',
                service: 'matters',
                endpoint: 'Matter/GetAllCompleteBySearchCriteriaAsCsv',
                data: data,
                suppressErrorToast: true
              })) || {}
          } catch (err) {
            if (err.status === 422) {
              alert(
                `You have selected ${formatThousands(mattersTableFilters.totalRowCount || 0)} records, but this feature is limited to ${formatThousands(10000)} records. Please refine your search to include fewer results, or contact support@ospreyapproach.com if you require further assistance`
              )
              return
            } else {
              alert('Error:', err)
            }
            console.log('Error:', err)
            return
          }

          const blob = new Blob([fetchedMattersTableDataCSV], { type: 'text/csv' })

          const blobUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = 'matter_export.csv'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          URL.revokeObjectURL(blobUrl)

          resolve(fetchedMattersTableDataCSV)
        }
      })
    })
  },
  reset: () => set(initialState)
}))
